import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { TableOfContents, Section, SectionHeader, CTABand, FAQ, StatRow, Breadcrumbs, InlineCTA, FeatureGrid } from "@/components/site/Blocks";
import { ProsCons, TLDR, ExpertInsight } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/dscr-rate-term-refinance";
const HERO = getDscrClusterHero(URL);

const KEYWORDS = [
  "DSCR rate and term refinance",
  "refinance hard money to DSCR",
  "refinance bridge loan to permanent",
  "rental property refinance no income verification",
  "investment property rate and term refinance",
  "DSCR refinance rates",
  "refinance balloon payment rental",
  "refinance interest only rental loan",
  "DSCR refinance LLC",
  "lower payment rental property refinance",
  "rate and term refinance LTV investment property",
].join(", ");

const FAQS = [
  {
    q: "What counts as a rate-and-term refinance?",
    a: "Paying off the existing lien plus closing costs and prepaid escrows, with incidental cash back generally limited to the lesser of 1% of the new loan amount or $2,000. Anything above that is treated as cash-out.",
  },
  {
    q: "How much leverage can I get?",
    a: "Rate-and-term follows purchase leverage more closely than cash-out — up to 80% LTV at the strongest tier, subject to FICO, DSCR, loan size, and property type.",
  },
  {
    q: "Can I refinance a hard money loan into a DSCR loan?",
    a: "Yes, and it is one of the most common uses. Once the rehab is complete and the property is rent-ready or leased, the DSCR loan takes out the short-term debt with 30-year financing.",
  },
  {
    q: "Do I need a lease in place?",
    a: "Not always. The appraiser's Form 1007 market rent can be used where there is no lease, though a vacant-property refinance typically requires DSCR of at least 1.00.",
  },
  {
    q: "Will I pay a prepayment penalty on my existing loan?",
    a: "That depends on your current note. We check the payoff statement early so the penalty is priced into the decision before you spend money on an appraisal.",
  },
  {
    q: "Can I do a rate-and-term refinance to remove a partner from title?",
    a: "Typically no — removing a co-borrower or partner and adjusting equity between owners is usually treated as cash-out even without net proceeds to you, since it changes the ownership structure rather than simply replacing the existing lien.",
  },
  {
    q: "Does a rate-and-term refinance reset my seasoning clock?",
    a: "It resets the ownership seasoning clock for a future cash-out refinance, since the seasoning period runs from your acquisition date and continues regardless of whether you've refinanced the loan itself in the meantime.",
  },
  {
    q: "Can I combine two liens into one with a rate-and-term refinance?",
    a: "Yes, paying off a first mortgage and a subordinate lien such as a HELOC together is a common rate-and-term use case, as long as the combined proceeds are limited to payoff, closing costs, and prepaid escrows.",
  },
  {
    q: "Will my new DSCR ratio be recalculated on a rate-and-term refinance?",
    a: "Yes. The refinance is underwritten on the current rent against the new PITIA at the new rate and term, so your ratio can improve, worsen, or stay similar depending on how the new payment compares to the old one.",
  },
  {
    q: "Is a rate-and-term refinance faster than a cash-out refinance?",
    a: "Timelines are generally similar since both require an appraisal and full underwriting, though rate-and-term files can move slightly faster because there's no cash-out seasoning analysis or use-of-proceeds documentation to review.",
  },
  {
    q: "Can I refinance a property that currently has no tenant?",
    a: "Yes, using the appraiser's market rent from Form 1007 or Form 1025, though a vacant property refinance typically needs to clear a minimum DSCR — generally 1.00 — since there's no in-place lease to support the file.",
  },
  {
    q: "Can I do a rate-and-term refinance if I already have a prepayment penalty on the current loan?",
    a: "Yes, though the existing penalty on your current note still applies and should be factored into whether refinancing now makes sense versus waiting for the penalty period to expire or step down.",
  },
];

export const Route = createFileRoute("/dscr-rate-term-refinance")({
  head: () =>
    buildHead({
      title: "DSCR Rate & Term Refinance — Exit Hard Money Into 30-Year Financing",
      description:
        "Refinance a rental out of hard money, a maturing bridge, or a high-rate note into 30-year DSCR financing. Up to 80% LTV, no tax returns, LLC vesting.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "Rate & Term Refinance", url: URL },
      ],
      service: { name: "DSCR Rate and Term Refinance" },
      faqs: FAQS,
    }),
  component: RateTerm,
});

function RateTerm() {
  return (
    <SiteShell hideBreadcrumbs productLabel="DSCR Rate & Term">
      <PageHero
        eyebrow="DSCR · Rate & Term Refinance"
        title="DSCR rate & term refinance"
        subtitle="Replace short-term or expensive debt with permanent rental financing. Because you are not taking equity out, rate-and-term keeps the highest leverage and the best pricing available on a DSCR refinance."
        image={HERO}
        primaryHref="/apply?product=dscr"
      />
      <PageEssentials hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "Rate & Term Refinance" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Snapshot" },
          { href: "#use-cases", label: "When to use it" },
          { href: "#vs-cashout", label: "Rate & term vs. cash-out" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <TLDR
        id="tldr"
        points={[
          "A DSCR rate & term refinance replaces your existing loan without pulling meaningful cash out — pricing and LTV are better than cash-out.",
          "Common uses: exiting hard money or a bridge loan, dropping an ARM into a fixed, or removing a partner's financing.",
          "Standard maximum LTV runs to 80% with qualifying DSCR and credit, versus 75% on cash-out.",
          "Qualification is still property-based: rent versus PITIA, credit, reserves, and appraisal.",
          "Terms shown are illustrative program ranges, not a quote or commitment to lend.",
        ]}
      />

      <Section id="snapshot">
        <StatRow
          items={[
            { label: "Max LTV", value: "Up to 80%" },
            { label: "Min FICO", value: "660" },
            { label: "Terms", value: "30-yr fixed / IO / ARM" },
            { label: "Cash back", value: "Incidental only" },
          ]}
        />
        <p className="mt-6 max-w-3xl text-muted-foreground">
          Rate-and-term means the new loan pays off the existing lien, closing costs, and escrows — nothing more. That
          narrower purpose is exactly why lenders allow more leverage and price it below a cash-out.
        </p>
      </Section>

      <div className="bg-muted/40">
        <Section id="use-cases">
          <SectionHeader eyebrow="When to use it" title="The four situations this solves" />
          <div className="mt-8">
            <FeatureGrid
              items={[
                { title: "Hard money takeout", body: "Rehab is done and the unit is leased — swap 11% interest-only for 30-year amortized rental debt." },
                { title: "Bridge maturity", body: "A 12–18 month bridge is coming due. Refinance before the balloon instead of negotiating an extension fee." },
                { title: "Rate improvement", body: "Pricing has moved since you closed and the new payment materially improves monthly cash flow." },
                { title: "Structure change", body: "Move from an ARM to fixed, add an interest-only period, or reset the amortization to 30 or 40 years." },
              ]}
            />
          </div>
        </Section>
      </div>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <LiveDscrRateSection
            strategyCards
        scenarioOverrides={{ loanPurpose: "rate_term_refinance" }}
        toolLabel="DSCR rate-and-term refinance"
        id="live-rates"
        eyebrow={"Live pricing · Rate & term"}
        heading={"Current DSCR Rate & Term Refinance Rates"}
        paragraphs={[
            "Rate-and-term refinances price closest to purchase pricing on the DSCR grid because no equity is being released. The live options below reflect the most recent successful pricing response; each row's rate, APR, points or credit and monthly principal-and-interest come from one Optimal Blue quote rather than being assembled from separate sources.",
            "Most rate-and-term files here are exits from hard money, a maturing bridge note, or a higher-cost private loan. When you compare options, look at the APR and the primary lender fee alongside the rate — a slightly higher rate with a lender credit often nets out better on a short hold than buying the rate down with points.",
        ]}
        faqs={[
            { q: "Can I roll closing costs into a DSCR rate and term refinance?", a: "Financing reasonable closing costs and prepaid items is generally permitted within program LTV limits. Adding meaningful cash proceeds converts the file to a cash-out refinance and prices accordingly." },
            { q: "Does paying off a hard money loan count as rate and term?", a: "Paying off the existing lien plus customary closing costs typically qualifies as rate and term. Seasoning and the original purpose of the lien being retired are reviewed by the lender." },
        ]}
        links={[
            { to: "/investment-property-rates", label: "See today's live DSCR pricing" },
            { to: "/dscr-cash-out-refinance", label: "Compare DSCR cash-out refinance" },
            { to: "/dscr-loan-requirements", label: "Review DSCR refinance requirements" },
        ]}
      />

      <Section id="vs-cashout">
        <SectionHeader eyebrow="Side by side" title="Rate & term vs. cash-out" />
        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Factor</th>
                <th className="px-4 py-3">Rate &amp; term</th>
                <th className="px-4 py-3">Cash-out</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                ["Maximum LTV", "Up to 80%", "Up to 75%"],
                ["Relative pricing", "Lower", "Higher"],
                ["Cash to borrower", "Incidental only", "Unrestricted business use"],
                ["Reserve requirement", "Standard", "Often one tier higher"],
                ["Typical use", "Debt replacement", "Funding the next acquisition"],
              ].map((row) => (
                <tr key={row[0]}>
                  <td className="px-4 py-3 font-semibold text-foreground">{row[0]}</td>
                  <td className="px-4 py-3 text-brand">{row[1]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <ProsCons
        title="Rate & term: benefits and trade-offs"
        pros={[
          "Highest available refinance leverage on a DSCR file",
          "Better pricing than an equivalent cash-out",
          "Clean exit from hard money or bridge before a balloon",
          "No income documentation and no DTI test",
        ]}
        cons={[
          "Cash back is limited to incidental amounts",
          "Payoff-statement prepayment penalties can erode the benefit",
          "A new appraisal and rent schedule are still required",
          "Closing costs reset — worth modeling against your hold period",
        ]}
      />

      <InlineCTA
        variant="soft"
        title="Have a bridge or hard money loan maturing?"
        body="Send the payoff date and current rent. We'll model the takeout and tell you whether the timing works."
        href="/apply?product=dscr"
      />


      <DscrProgramLinks currentPath={URL} />

      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="Rate & term refinance questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Business-purpose loans only. Figures are illustrative, subject to change, and are not a quote, offer, or
          commitment to lend. Eligibility, terms, and availability vary by borrower, property, lender, and state.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="dscr-rate-term" />

      <CTABand
        title="Refinance into permanent rental financing"
        body="Same-business-day scenario review on complete submissions."
        href="/apply?product=dscr"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
